import { useState, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { plan_top_down_update_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import {
  type OrgNode,
  buildOrgNodeOptions,
  formatPeriod,
  csrfToken,
  slugify,
  sanitizeSlugInput,
} from './planShared'

export interface EditablePlan {
  name: string
  slug: string
  begin_date: string
  end_date: string
  org_node_id: string | null
}

export default function EditPlanModal({
  plan,
  orgNodes,
  onClose,
  onSaved,
}: {
  plan: EditablePlan
  orgNodes: OrgNode[]
  onClose: () => void
  onSaved: (newSlug: string) => void
}) {
  const market = useMarket()
  const orgNodeOptions = useMemo(() => buildOrgNodeOptions(orgNodes), [orgNodes])
  const [name, setName] = useState(plan.name)
  const [slug, setSlug] = useState(plan.slug)
  const [slugTouched, setSlugTouched] = useState(false)
  const [orgNodeId, setOrgNodeId] = useState(plan.org_node_id ?? '')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleNameChange = (v: string) => {
    setName(v)
    if (!slugTouched) setSlug(slugify(v))
  }

  const handleSlugChange = (v: string) => {
    setSlugTouched(true)
    setSlug(sanitizeSlugInput(v))
  }
  const handleSlugBlur = () => setSlug((s) => slugify(s))

  const handleSubmit = useCallback(async () => {
    const trimmed = name.trim()
    const trimmedSlug = slug.trim()
    if (!trimmed) {
      setError('Name is required.')
      return
    }
    if (!trimmedSlug) {
      setError('Slug is required.')
      return
    }
    if (!orgNodeId) {
      setError('Scope is required.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch(plan_top_down_update_path(market.slug, plan.slug), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: JSON.stringify({ name: trimmed, new_slug: trimmedSlug, org_node_id: orgNodeId }),
      })
      if (res.ok) {
        const data = await res.json().catch(() => null)
        onClose()
        onSaved(data?.slug ?? trimmedSlug)
      } else {
        const data = await res.json().catch(() => null)
        setError(data?.error || 'Something went wrong.')
        setSubmitting(false)
      }
    } catch {
      setError('Network error.')
      setSubmitting(false)
    }
  }, [market.slug, plan.slug, name, slug, orgNodeId, onClose, onSaved])

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--foreground)', marginBottom: 4 }}>Edit Plan</div>
        <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginBottom: 18 }}>{formatPeriod(plan.begin_date, plan.end_date)}</div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', marginBottom: 4 }}>Name</label>
          <Input
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', marginBottom: 4 }}>Slug</label>
          <Input
            type="text"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            onBlur={handleSlugBlur}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', marginBottom: 4 }}>Scope</label>
          <select
            value={orgNodeId}
            onChange={(e) => setOrgNodeId(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs transition-colors"
          >
            <option value="">Select a scope…</option>
            {orgNodeOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        {error && (
          <div style={{ fontSize: 11, color: 'var(--destructive)', marginBottom: 12, padding: '6px 10px', background: 'var(--destructive-foreground)', borderRadius: 5 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="secondary" size="sm" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}
